/**
 * Referrals API — balance, deep link, list of invitees, payout requests.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, type ApiError } from "./client";

// ============================================================
// Types
// ============================================================

export interface ReferralStats {
  balance_usd: number;
  total_earned_usd: number;
  referrals_count: number;
  paid_referrals_count: number;
  deep_link: string;
  bot_username: string;
  payout_min_usd: number;
  reward_pct: number;
}

export interface ReferralItem {
  id: number;
  first_name: string | null;
  username: string | null;
  joined_at: string;
  has_paid: boolean;
}

export interface ReferralList {
  items: ReferralItem[];
  total: number;
}

export interface Payout {
  id: number;
  amount_usd: number;
  status: "pending" | "paid" | "rejected";
  note: string | null;
  created_at: string;
  resolved_at: string | null;
}

export interface PayoutsList {
  items: Payout[];
}

// ============================================================
// Queries
// ============================================================

export function useReferralStats() {
  return useQuery<ReferralStats, ApiError>({
    queryKey: ["referrals", "stats"],
    queryFn: () => api.get<ReferralStats>("/referrals/stats"),
    staleTime: 30_000,
  });
}

export function useReferralList(params?: { limit?: number; offset?: number }) {
  const limit = params?.limit ?? 50;
  const offset = params?.offset ?? 0;
  return useQuery<ReferralList, ApiError>({
    queryKey: ["referrals", "list", limit, offset],
    queryFn: () => api.get<ReferralList>(`/referrals/list?limit=${limit}&offset=${offset}`),
    staleTime: 30_000,
  });
}

export function usePayouts() {
  return useQuery<PayoutsList, ApiError>({
    queryKey: ["referrals", "payouts"],
    queryFn: () => api.get<PayoutsList>("/referrals/payouts"),
    staleTime: 15_000,
  });
}

// ============================================================
// Mutations
// ============================================================

export function useRequestPayout() {
  const qc = useQueryClient();
  return useMutation<Payout, ApiError, { amount_usd: number; note?: string | null }>({
    mutationFn: (body) => api.post<Payout>("/referrals/payout", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["referrals", "stats"] });
      qc.invalidateQueries({ queryKey: ["referrals", "payouts"] });
    },
  });
}
